import { useCallback, useEffect, useRef, useState } from "react";
import type { OtcPopupProduct } from "data-types";
import * as http from "../../../utils/http";

export type OtcPopupSearchStatus = "idle" | "loading" | "ok" | "empty" | "error";

const MIN_CHARS = 2;
const DEBOUNCE_MS = 300;

export const useOtcPopupSearch = (country: string, language: string) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<OtcPopupProduct[]>([]);
  const [resultCount, setResultCount] = useState(0);
  const [status, setStatus] = useState<OtcPopupSearchStatus>("idle");
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelInFlight = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  useEffect(() => {
    const trimmed = query.trim();
    cancelInFlight();
    if (trimmed.length < MIN_CHARS) {
      setResults([]);
      setResultCount(0);
      setStatus("idle");
      return;
    }
    const timer = setTimeout(async () => {
      const controller = new AbortController();
      abortControllerRef.current = controller;
      setStatus("loading");
      try {
        const response: any = await http.getFromServer(
          `v2/otc-popup/search/${country}/${language}?q=${encodeURIComponent(trimmed)}`,
          [http.MIDDLEWARES.CUSTOMER_ID, http.MIDDLEWARES.CART_ORDER],
          { signal: controller.signal },
        );
        if (controller.signal.aborted) return;
        const products: OtcPopupProduct[] = response?.data?.products ?? [];
        setResults(products);
        setResultCount(response?.data?.search_result_count ?? products.length);
        setStatus(products.length > 0 ? "ok" : "empty");
      } catch (err: any) {
        if (controller.signal.aborted || err?.name === "CanceledError" || err?.code === "ERR_CANCELED") return;
        setResults([]);
        setResultCount(0);
        setStatus("error");
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, country, language, cancelInFlight]);

  useEffect(() => cancelInFlight, [cancelInFlight]);

  const clear = useCallback(() => {
    cancelInFlight();
    setQuery("");
    setResults([]);
    setResultCount(0);
    setStatus("idle");
  }, [cancelInFlight]);

  return { query, setQuery, results, status, resultCount, clear };
};
